import { useCallback, useMemo } from 'react';
import { useSelectorPreferences, useSelectorPreferencesOwner } from '@/hooks/use-selector-preferences';
import type { ProjectWithMetadata } from '@/types/project-selector';
import type { Project } from '@/types';

const STORAGE_KEY_PREFIX = 'project-selector-state';
const MAX_RECENT_PROJECTS = 10;

function getStorageKey(companyId?: string): string {
  return companyId ? `${STORAGE_KEY_PREFIX}-${companyId}` : STORAGE_KEY_PREFIX;
}


/**
 * Hook to manage project selector state including favourites and recently used projects.
 * Inside a SelectorPreferencesOwnerContext the state belongs to that employee and is stored server-side.
 */
export function useProjectSelector(projects: Project[] | ProjectWithMetadata[], companyId?: string) {
  const owner = useSelectorPreferencesOwner();
  const [state, setState] = useSelectorPreferences('project', getStorageKey(companyId), owner);

  // Toggle favourite status
  const toggleFavourite = useCallback((projectId: string) => {
    setState((prev) => {
      const isFavourite = prev.favourites.includes(projectId);
      return {
        ...prev,
        favourites: isFavourite
          ? prev.favourites.filter((id) => id !== projectId)
          : [...prev.favourites, projectId],
      };
    });
  }, [setState]);

  // Record project access
  const recordProjectAccess = useCallback((projectId: string) => {
    setState((prev) => {
      // Remove existing entry if present
      const filtered = prev.recentlyUsed.filter((item) => item.id !== projectId);
      
      // Add to front with current timestamp
      const updated = [
        { id: projectId, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_RECENT_PROJECTS);

      return {
        ...prev,
        recentlyUsed: updated,
      };
    });
  }, [setState]);

  // Get enriched projects with favourite and recently used metadata
  const enrichedProjects = useMemo(() => {
    return projects.map((project) => ({
      ...project,
      isFavourite: state.favourites.includes(project.id),
      lastAccessedDate: state.recentlyUsed.find((item) => item.id === project.id)
        ? new Date(state.recentlyUsed.find((item) => item.id === project.id)!.timestamp)
        : undefined,
    }));
  }, [projects, state.favourites, state.recentlyUsed]);

  // Get favourite projects
  const favouriteProjects = useMemo(() => {
    return enrichedProjects
      .filter((p) => p.isFavourite)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [enrichedProjects]);

  // Get recently used projects (excluding favourites)
  const recentProjects = useMemo(() => {
    const recentIds = state.recentlyUsed.map((item) => item.id);
    return enrichedProjects
      .filter((p) => recentIds.includes(p.id) && !p.isFavourite)
      .sort((a, b) => {
        const aTime = a.lastAccessedDate?.getTime() || 0;
        const bTime = b.lastAccessedDate?.getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, MAX_RECENT_PROJECTS);
  }, [enrichedProjects, state.recentlyUsed]);

  return {
    enrichedProjects,
    favouriteProjects,
    recentProjects,
    toggleFavourite,
    recordProjectAccess,
    isFavourite: (projectId: string) => state.favourites.includes(projectId),
  };
}
